
import React, { useState, useContext, useMemo } from 'react';
import { AppContext, supabase, SUPABASE_URL, SUPABASE_ANON_KEY } from '../contexts/AppContext';
import { useUI } from '../contexts/UIContext';
import { Socio, Rol } from '../types';
import Modal from '../components/Modal';
import ImportSociosWizard from '../components/ImportSociosWizard';

const SocioForm: React.FC<{ socio: Partial<Socio>; onSave: (socio: Partial<Socio>) => void; onCancel: () => void; }> = ({ socio, onSave, onCancel }) => {
    const [formData, setFormData] = useState(socio);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input type="text" name="nombre" placeholder="Nombre" value={formData.nombre || ''} onChange={handleChange} className="w-full p-2 border rounded" required />
                <input type="text" name="apellido" placeholder="Apellido" value={formData.apellido || ''} onChange={handleChange} className="w-full p-2 border rounded" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <input type="text" name="documento" placeholder="DNI (Obligatorio)" value={formData.documento || ''} onChange={handleChange} className="w-full p-2 border rounded" required />
                <input type="text" name="telefono" placeholder="Teléfono (Obligatorio)" value={formData.telefono || ''} onChange={handleChange} className="w-full p-2 border rounded" required />
            </div>
            <input type="email" name="email" placeholder="Email (Obligatorio)" value={formData.email || ''} onChange={handleChange} className="w-full p-2 border rounded" required />
            <input type="text" name="instagram" placeholder="Instagram" value={formData.instagram || ''} onChange={handleChange} className="w-full p-2 border rounded" />
            <textarea name="notas" placeholder="Notas" value={formData.notas || ''} onChange={handleChange} className="w-full p-2 border rounded" />
            <select name="estado" value={formData.estado || 'activo'} onChange={handleChange} className="w-full p-2 border rounded">
                <option value="activo">Activo</option>
                <option value="inactivo">Inactivo</option>
            </select>
            <div className="flex justify-end gap-2">
                <button type="button" onClick={onCancel} className="bg-gray-300 text-black px-4 py-2 rounded hover:bg-gray-400">Cancelar</button>
                <button type="submit" className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700">Guardar</button>
            </div>
        </form>
    );
};


const SociosScreen: React.FC = () => {
    const { user, socios, addSocio, updateSocio, deleteSocio, hardDeleteSocio, movimientosPuntos, deudas, setActiveScreen, isReadOnly } = useContext(AppContext);

    // Total adeudado por cada socio (suma de deudas pendientes).
    const deudaPorSocio = useMemo(() => {
        const m = new Map<string, number>();
        (deudas || []).forEach(d => {
            if (d.estado === 'pendiente') {
                const k = String(d.socio_id);
                m.set(k, (m.get(k) || 0) + Number(d.monto));
            }
        });
        return m;
    }, [deudas]);
    const [ventaModalOpen, setVentaModalOpen] = useState(false);
    const [ventaDetalle, setVentaDetalle] = useState<any | null>(null);

    const openVentaModal = async (ventaId: number) => {
        try {
            setVentaDetalle(null);
            setVentaModalOpen(true);
            const { data, error } = await supabase.from('ventas').select('*, items:venta_items(*, producto:productos(*))').eq('id', ventaId).single();
            if (error) throw error;
            setVentaDetalle(data);
        } catch (err: any) {
            console.error('Error fetching venta:', err);
            setVentaDetalle(null);
        }
    };

    const closeVentaModal = () => { setVentaDetalle(null); setVentaModalOpen(false); };
    const ui = useUI();
    const [searchTerm, setSearchTerm] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedSocio, setSelectedSocio] = useState<Socio | null>(null);
    const [viewingSocio, setViewingSocio] = useState<Socio | null>(null);
    const [expandedSocioId, setExpandedSocioId] = useState<number | null>(null);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [socioToDelete, setSocioToDelete] = useState<Socio | null>(null);
    const [socioToHardDelete, setSocioToHardDelete] = useState<Socio | null>(null);
    const [hardDeleteConfirmText, setHardDeleteConfirmText] = useState('');
    const [sendingLinkFor, setSendingLinkFor] = useState<number | null>(null);
    const [importingMode, setImportingMode] = useState(false);

    const filteredSocios = useMemo(() =>
        socios.filter(s =>
            (s.nombre || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (s.apellido || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (s.documento || '').includes(searchTerm)
        ), [socios, searchTerm]
    );

    const handleOpenModal = (socio?: Socio) => {
        setSelectedSocio(socio || null);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedSocio(null);
    };

    const handleSaveSocio = async (socioData: Partial<Socio>) => {
        try {
            let success = false;
            // Sanitize documento: trim and convert empty string to null
            const documentoRaw = typeof socioData.documento === 'string' ? socioData.documento.trim() : socioData.documento;
            const documento = documentoRaw ? documentoRaw : null;

            if (selectedSocio) {
                // If editing, ensure documento (if provided) doesn't collide with another socio
                if (documento) {
                    const conflict = socios.find(s => s.documento === documento && s.id !== selectedSocio.id);
                    if (conflict) {
                        ui.showModal('Error', 'Ya existe un socio con ese documento.');
                        return;
                    }
                }
                success = await updateSocio({ ...selectedSocio, ...socioData, documento } as Socio);
            } else {
                // If creating, check client-side for duplicates to avoid round-trip
                if (documento) {
                    const exists = socios.find(s => s.documento === documento);
                    if (exists) {
                        ui.showModal('Error', 'Ya existe un socio con ese documento.');
                        return;
                    }
                }
                const result = await addSocio({ ...socioData, documento } as Omit<Socio, 'id' | 'creado_por' | 'creado_en' | 'puntos' | 'club_id'>);
                success = !!result;
            }
            
            if (success) {
                handleCloseModal();
            }
        } catch (error: any) {
            ui.showModal('Error', error.message || 'Error inesperado');
        }
    };
    
    const handleDeleteClick = (socio: Socio) => {
        setSocioToDelete(socio);
    };

    const sendAccessLink = async (socio: Socio) => {
        if (!socio.email) {
            ui.showToast('error', 'El socio no tiene un email registrado.');
            return;
        }
        try {
            setSendingLinkFor(socio.id);
            // 1) Asegurarnos que exista un Auth user (Edge Function con service_role_key)
            try {
                const fnUrl = `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/ensure-auth-user`;
                const ensureResp = await fetch(fnUrl, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        apikey: SUPABASE_ANON_KEY || '',
                        Authorization: `Bearer ${SUPABASE_ANON_KEY || ''}`
                    },
                    body: JSON.stringify({ email: socio.email, nombre: socio.nombre })
                });
                const ensureJson = await ensureResp.json().catch(() => ({}));
                if (ensureJson.error) {
                    ui.showToast('error', 'No se pudo asegurar el usuario en Auth.');
                    setSendingLinkFor(null);
                    return;
                }
            } catch (e) {
                console.error('Error calling ensure-auth-user:', e);
                ui.showToast('error', 'No se pudo comunicar con la función de servidor para crear el usuario.');
                setSendingLinkFor(null);
                return;
            }

            const redirectTo = window.location.origin + '/';
            const res: any = await supabase.auth.signInWithOtp({ email: socio.email, options: { emailRedirectTo: redirectTo } });

            if (res.error) {
                ui.showToast('error', res.error.message || 'No se pudo enviar el enlace.');
            } else {
                // Si la respuesta no contiene información clara, alertamos sobre posible falta de SMTP/Redirects.
                const hasData = res.data && Object.keys(res.data).length > 0;
                if (hasData) {
                    ui.showToast('success', 'Se solicitó el envío del enlace. Revisa el email del socio.');
                } else {
                    ui.showToast('error', 'No se pudo verificar el envío. Revisá la configuración de SMTP y Redirect URLs en Supabase (ver consola para detalles).');
                }
            }
        } catch (err: any) {
            ui.showToast('error', err?.message || 'Error enviando enlace');
        } finally {
            setSendingLinkFor(null);
        }
    };

    const handleHardDeleteClick = (socio: Socio) => {
        setSocioToHardDelete(socio);
        setHardDeleteConfirmText('');
    };

    const closeHardDeleteModal = () => {
        setSocioToHardDelete(null);
        setHardDeleteConfirmText('');
    };

    const confirmHardDelete = async () => {
        if (!socioToHardDelete) return;
        const id = socioToHardDelete.id;
        setDeletingId(id);
        closeHardDeleteModal();

        const result = await hardDeleteSocio(id);
        if (!result.success) {
            ui.showModal('Error', result.message || 'Error al eliminar el socio.');
        } else {
            ui.showToast('success', 'Socio eliminado definitivamente.');
        }
        setDeletingId(null);
    };

    const confirmDelete = async () => {
        if (!socioToDelete) return;
        const id = socioToDelete.id;
        setDeletingId(id);
        setSocioToDelete(null); // Cerrar modal

        const result = await deleteSocio(id);
        if (!result.success) {
            ui.showModal('Error', result.message || "Error al inactivar el socio.");
        }
        setDeletingId(null);
    };
    
    const canEdit = (user?.rol === Rol.ADMIN || user?.rol === Rol.CAJERO) && !isReadOnly;
    const canDelete = user?.rol === Rol.ADMIN && !isReadOnly;
    const canImport = user?.rol === Rol.ADMIN && !isReadOnly;

    const toggleExpand = (id: number) => {
        setExpandedSocioId(prevId => prevId === id ? null : id);
    };

    if (importingMode) {
        return <ImportSociosWizard onClose={() => setImportingMode(false)} />;
    }

    if(viewingSocio) {
        const socioMovimientos = movimientosPuntos.filter(m => m.socio_id === viewingSocio.id).sort((a,b) => new Date(b.creado_en).getTime() - new Date(a.creado_en).getTime());
        return (
            <div className="bg-white rounded-lg shadow-md">
                <div className="p-4 md:p-6">
                    <div className="flex justify-between items-start mb-6">
                        <div>
                            <h2 className="text-2xl font-bold text-gray-800">{viewingSocio.nombre} {viewingSocio.apellido}</h2>
                            <p className="text-gray-600">DNI: {viewingSocio.documento} | Tel: {viewingSocio.telefono}</p>
                            <p className="text-blue-600 font-bold text-lg mt-2">Puntos: {viewingSocio.puntos}</p>
                        </div>
                        <button onClick={() => setViewingSocio(null)} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-300 font-semibold">&larr; Volver al listado</button>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-4">Historial de Puntos</h3>
                </div>
                <div className="overflow-x-auto">
                    <div className="align-middle inline-block min-w-full">
                        <div className="border-t border-b border-gray-200">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Fecha</th>
                                        <th scope="col" className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tipo</th>
                                        <th scope="col" className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Puntos</th>
                                        <th scope="col" className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Descripción</th>
                                        <th scope="col" className="px-4 md:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Realizado por</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {socioMovimientos.map(mov => {
                                        const match = /venta_id[:\s]*([0-9]+)/i.exec(String(mov.descripcion || ''));
                                        return (
                                            <tr key={mov.id}>
                                                <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-gray-600">{new Date(mov.creado_en).toLocaleDateString('es-AR')}</td>
                                                <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-gray-600 capitalize">{mov.tipo}</td>
                                                <td className={`px-4 md:px-6 py-4 whitespace-nowrap text-sm font-bold ${mov.puntos > 0 ? 'text-green-600' : 'text-red-600'}`}>{mov.puntos}</td>
                                                <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                    {match ? (
                                                        <button onClick={() => openVentaModal(Number(match[1]))} className="text-blue-600 underline">{mov.descripcion.replace(/\(venta_id:?\s*\d+\)/i, `(venta #${match[1]})`)}</button>
                                                    ) : mov.descripcion}
                                                </td>
                                                <td className="px-4 md:px-6 py-4 whitespace-nowrap text-sm text-gray-600">{mov.creado_por}</td>
                                            </tr>
                                        )
                                    })}
                                    {socioMovimientos.length === 0 && (
                                        <tr><td colSpan={5} className="text-center px-4 md:px-6 py-6 text-gray-500">Este socio no tiene movimientos de puntos.</td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
                <Modal isOpen={ventaModalOpen} onClose={closeVentaModal} title={ventaDetalle ? `Venta #${ventaDetalle.id}` : 'Cargando venta...'}>
                    {ventaDetalle ? (
                        <div className="space-y-3">
                            <div className="text-sm text-gray-600">Cajero: {ventaDetalle.creado_por}</div>
                            <div className="text-sm text-gray-600">Medio de pago: {ventaDetalle.medio_de_pago}</div>
                            <div className="pt-2">
                                <p className="text-sm font-medium text-gray-600">Items:</p>
                                <ul className="list-disc list-inside text-sm text-gray-600 mt-1 pl-2">
                                    {ventaDetalle.items.map((it: any) => (
                                        <li key={it.id}>{it.cantidad} x {it.producto?.nombre || 'Producto'} - ${(it.cantidad * it.precio_unitario).toLocaleString('es-AR')}</li>
                                    ))}
                                </ul>
                            </div>
                            <div className="flex justify-between text-sm text-gray-700 font-medium mt-2">
                                <span>Total antes del canje</span>
                                <span>${ventaDetalle.items.reduce((s: number, it: any) => s + (it.cantidad * it.precio_unitario), 0).toLocaleString('es-AR')}</span>
                            </div>
                            {ventaDetalle.puntos_canjeados > 0 && (
                                <div className="flex justify-between text-sm text-red-600">
                                    <span>Puntos canjeados</span>
                                    <span>-${ventaDetalle.puntos_canjeados.toLocaleString('es-AR')}</span>
                                </div>
                            )}
                            <div className="flex justify-between text-sm text-gray-900 font-bold">
                                <span>Total final</span>
                                <span>${ventaDetalle.total.toLocaleString('es-AR')}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-6">Cargando...</div>
                    )}
                </Modal>
            </div>
        )
    }

    return (
        <div className="bg-white rounded-lg shadow-md">
            <div className="p-4 md:p-6">
                <div className="flex flex-wrap gap-4 justify-between items-center">
                    <h2 className="text-2xl font-bold text-gray-800">Gestión de Socios</h2>
                    <div className="flex gap-4 items-center w-full sm:w-auto">
                        <input
                            type="text"
                            placeholder="Buscar por nombre, apellido o documento..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className="flex-grow sm:flex-grow-0 sm:w-72 p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        {canImport && (
                            <button
                                onClick={() => setImportingMode(true)}
                                className="bg-white border border-blue-600 text-blue-600 px-4 py-2 rounded-md hover:bg-blue-50 font-semibold whitespace-nowrap flex items-center gap-1"
                                title="Importar socios desde un CSV de Google Contacts"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" /></svg>
                                Importar
                            </button>
                        )}
                        <button onClick={() => handleOpenModal()} disabled={!canEdit} className="bg-blue-600 text-white px-5 py-2 rounded-md hover:bg-blue-700 font-semibold whitespace-nowrap disabled:bg-gray-400">Nuevo Socio</button>
                    </div>
                </div>
            </div>

            <div className="border-t border-gray-200">
                 {/* Desktop Table View */}
                 <div className="hidden md:block">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Nombre</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contacto</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Puntos</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estado</th>
                                <th scope="col" className="relative px-6 py-3"><span className="sr-only">Acciones</span></th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredSocios.map(socio => {
                                const debe = deudaPorSocio.get(String(socio.id)) || 0;
                                return (
                                <tr key={socio.id}>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-sm font-medium text-gray-900">{socio.nombre} {socio.apellido}</span>
                                            {debe > 0 && (
                                                <button
                                                    onClick={() => setActiveScreen('deudas')}
                                                    title="Ver deudas pendientes"
                                                    className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full hover:bg-red-200"
                                                >
                                                    Debe ${debe.toLocaleString('es-AR')}
                                                </button>
                                            )}
                                        </div>
                                        <div className="text-sm text-gray-500">{socio.documento}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm text-gray-900">{socio.email}</div>
                                        <div className="text-sm text-gray-500">{socio.telefono}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-bold">{socio.puntos}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${socio.estado === 'activo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                            {socio.estado}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                                        <button onClick={() => setViewingSocio(socio)} className="text-blue-600 hover:text-blue-900">Historial</button>
                                        <button onClick={() => sendAccessLink(socio)} disabled={!canEdit || sendingLinkFor === socio.id} className="text-indigo-600 hover:text-indigo-900 disabled:text-gray-400 disabled:cursor-not-allowed">{sendingLinkFor === socio.id ? 'Enviando...' : 'Enviar enlace'}</button>
                                        <button onClick={() => handleOpenModal(socio)} disabled={!canEdit} className="text-yellow-600 hover:text-yellow-900 disabled:text-gray-400 disabled:cursor-not-allowed">Editar</button>
                                        {canDelete && (
                                            <button
                                                onClick={() => handleDeleteClick(socio)}
                                                disabled={deletingId === socio.id}
                                                className="text-yellow-600 hover:text-yellow-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                                            >
                                                {deletingId === socio.id ? '...' : 'Inactivar'}
                                            </button>
                                        )}
                                        {canDelete && (
                                            <button
                                                onClick={() => handleHardDeleteClick(socio)}
                                                disabled={deletingId === socio.id}
                                                className="text-red-600 hover:text-red-900 disabled:text-gray-400 disabled:cursor-not-allowed"
                                            >
                                                {deletingId === socio.id ? '...' : 'Eliminar'}
                                            </button>
                                        )}
                                    </td>
                                </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* Mobile Accordion View */}
                <div className="divide-y divide-gray-200 md:hidden">
                    {filteredSocios.map(socio => {
                        const isExpanded = expandedSocioId === socio.id;
                        return (
                            <div key={socio.id} className="p-4">
                                <button onClick={() => toggleExpand(socio.id)} className="w-full text-left">
                                    <div className="flex justify-between items-center">
                                        <div>
                                            <h3 className="text-md font-semibold text-gray-800">{socio.nombre} {socio.apellido}</h3>
                                            <p className="text-sm text-gray-500">{socio.documento}</p>
                                            {(deudaPorSocio.get(String(socio.id)) || 0) > 0 && (
                                                <span className="inline-block bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full mt-1">
                                                    Debe ${(deudaPorSocio.get(String(socio.id)) || 0).toLocaleString('es-AR')}
                                                </span>
                                            )}
                                        </div>
                                        <svg className={`w-5 h-5 text-gray-500 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path></svg>
                                    </div>
                                </button>
                                <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isExpanded ? 'max-h-96 mt-4' : 'max-h-0'}`}>
                                    <div className="space-y-3 pt-2 border-t mt-2">
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Teléfono</span>
                                            <span className="font-medium text-gray-800">{socio.telefono}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Email</span>
                                            <span className="font-medium text-gray-800">{socio.email}</span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-gray-500">Puntos</span>
                                            <span className="font-medium text-gray-800">{socio.puntos}</span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                             <span className="text-gray-500">Estado</span>
                                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full capitalize ${socio.estado === 'activo' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                {socio.estado}
                                            </span>
                                        </div>
                                        <div className="flex justify-end space-x-4 pt-4">
                                            <button onClick={() => setViewingSocio(socio)} className="text-blue-600 hover:text-blue-900 font-medium text-sm">Ver Historial</button>
                                            <button onClick={() => sendAccessLink(socio)} disabled={!canEdit || sendingLinkFor === socio.id} className="text-indigo-600 hover:text-indigo-900 font-medium text-sm disabled:text-gray-400 disabled:cursor-not-allowed">{sendingLinkFor === socio.id ? 'Enviando...' : 'Enviar enlace'}</button>
                                            <button onClick={() => handleOpenModal(socio)} disabled={!canEdit} className="text-yellow-600 hover:text-yellow-900 font-medium text-sm disabled:text-gray-400 disabled:cursor-not-allowed">Editar</button>
                                            {canDelete && (
                                                <button
                                                    onClick={() => handleDeleteClick(socio)}
                                                    disabled={deletingId === socio.id}
                                                    className="text-yellow-600 hover:text-yellow-900 font-medium text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
                                                >
                                                    {deletingId === socio.id ? '...' : 'Inactivar'}
                                                </button>
                                            )}
                                            {canDelete && (
                                                <button
                                                    onClick={() => handleHardDeleteClick(socio)}
                                                    disabled={deletingId === socio.id}
                                                    className="text-red-600 hover:text-red-900 font-medium text-sm disabled:text-gray-400 disabled:cursor-not-allowed"
                                                >
                                                    {deletingId === socio.id ? '...' : 'Eliminar'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {filteredSocios.length === 0 && (
                    <div className="px-4 md:px-6 py-6 text-center text-gray-500">
                        No se encontraron socios.
                    </div>
                )}
            </div>

            {isModalOpen && (
                <Modal isOpen={isModalOpen} onClose={handleCloseModal} title={selectedSocio ? 'Editar Socio' : 'Nuevo Socio'}>
                    <SocioForm socio={selectedSocio || {}} onSave={handleSaveSocio} onCancel={handleCloseModal} />
                </Modal>
            )}

            {/* Hard Delete Confirmation Modal */}
            {socioToHardDelete && (() => {
                const expected = `${socioToHardDelete.nombre || ''} ${socioToHardDelete.apellido || ''}`.trim();
                const matches = hardDeleteConfirmText.trim() === expected;
                return (
                    <Modal isOpen={!!socioToHardDelete} onClose={closeHardDeleteModal} title="Eliminar socio definitivamente">
                        <div className="space-y-4">
                            <div className="bg-red-50 border border-red-200 rounded p-3 text-sm text-red-800">
                                <p className="font-bold mb-1">⚠ Esta acción NO se puede deshacer</p>
                                <p>Se borrarán de forma permanente el socio, sus movimientos de puntos, sus ventas y los items de esas ventas. El historial contable se perderá.</p>
                            </div>
                            <p className="text-gray-700">
                                Para confirmar, escribí el nombre completo del socio:&nbsp;
                                <span className="font-bold">{expected}</span>
                            </p>
                            <input
                                type="text"
                                value={hardDeleteConfirmText}
                                onChange={e => setHardDeleteConfirmText(e.target.value)}
                                placeholder={expected}
                                className="w-full p-2 border rounded"
                                autoFocus
                            />
                            <div className="flex justify-end gap-3 pt-2">
                                <button
                                    onClick={closeHardDeleteModal}
                                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium"
                                >
                                    Cancelar
                                </button>
                                <button
                                    onClick={confirmHardDelete}
                                    disabled={!matches}
                                    className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                                >
                                    Eliminar definitivamente
                                </button>
                            </div>
                        </div>
                    </Modal>
                );
            })()}

            {/* Confirmation Modal */}
            {socioToDelete && (
                <Modal isOpen={!!socioToDelete} onClose={() => setSocioToDelete(null)} title="Confirmar Inactivación">
                    <div className="space-y-4">
                        <p className="text-gray-700">
                            ¿Querés inactivar al socio <span className="font-bold">{socioToDelete.nombre} {socioToDelete.apellido}</span>?
                        </p>
                        <p className="text-sm text-gray-600">El socio quedará marcado como <span className="font-semibold">inactivo</span> y no aparecerá en la búsqueda rápida de Caja. Esta acción se puede revertir desde la edición del socio.</p>
                        <div className="flex justify-end gap-3 pt-2">
                            <button 
                                onClick={() => setSocioToDelete(null)} 
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 font-medium"
                            >
                                Cancelar
                            </button>
                            <button 
                                onClick={confirmDelete} 
                                className="px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 font-medium"
                            >
                                Inactivar
                            </button>
                        </div>
                    </div>
                </Modal>
            )}
        </div>
    );
};

export default SociosScreen;
